// Fleet Service
// Handles all fleet operator-related API calls
import { v1Api, handleApiError, handleApiSuccess } from './api.js';
import apiService from './apiService.js';
import authService from './authService.js';
import { API_CONFIG } from '../config/api.js';

class FleetService {
  // ============================================================================
  // FLEET PROFILE MANAGEMENT
  // ============================================================================

  async getMyProfile() {
    return await authService.fleetGetProfile();
  }

  async updateMyProfile(profileData) {
    // PUT /v1/fleet/my-profile - request body must match API exactly
    try {
      const endpoint = API_CONFIG.FLEET_ENDPOINTS.MY_PROFILE;
      const prefs = profileData.partnership_preferences;
      const body = {
        name: profileData.name ?? '',
        contact_info: profileData.contact_info ?? '',
        address: profileData.address ?? '',
        city: profileData.city ?? '',
        state: profileData.state ?? '',
        pincode: profileData.pincode ?? '',
        emergency_contact: profileData.emergency_contact ?? '',
        service_area: profileData.service_area ?? '',
        auto_accept_partnerships: Boolean(profileData.auto_accept_partnerships),
        partnership_preferences: prefs && typeof prefs === 'object'
          ? {
              preferred_hospitals: Array.isArray(prefs.preferred_hospitals) ? prefs.preferred_hospitals : [],
              max_distance_km: Number(prefs.max_distance_km) || 0
            }
          : { preferred_hospitals: [], max_distance_km: 0 }
      };
      const response = await v1Api.put(endpoint, body);
      return handleApiSuccess(response);
    } catch (error) {
      throw handleApiError(error);
    }
  }

  async getAvailableHospitals(fleetId, params = {}) {
    return await authService.fleetGetAvailableHospitals(fleetId, params);
  }

  async getProfileById(operatorId) {
    try {
      const response = await apiService.get(`/api/fleet/profile/${operatorId}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateProfileById(operatorId, profileData) {
    try {
      const response = await apiService.put(`/api/fleet/profile/${operatorId}`, profileData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // FLEET DASHBOARD
  // ============================================================================

  async getMyDashboard(params = {}) {
    return await apiService.getMyFleetDashboard(params);
  }

  async getDashboardById(operatorId, params = {}) {
    return await apiService.getFleetDashboard(operatorId, params);
  }

  // ============================================================================
  // FLEET OPERATORS MANAGEMENT
  // ============================================================================

  async getAllOperators(params = {}) {
    return await apiService.getAllFleetOperatorsList(params);
  }

  async getOperatorById(operatorId) {
    try {
      const response = await apiService.get(`/api/fleet/operators/${operatorId}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // FLEET ANALYTICS
  // ============================================================================

  async getAnalytics(params = {}) {
    try {
      const response = await apiService.get('/fleet/analytics', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getPerformanceMetrics(params = {}) {
    try {
      const response = await apiService.get('/fleet/performance', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getRevenueAnalytics(params = {}) {
    try {
      const response = await apiService.get('/fleet/revenue', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getUtilizationMetrics(params = {}) {
    try {
      const response = await apiService.get('/fleet/utilization', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // FLEET DRIVERS MANAGEMENT
  // ============================================================================

  async getMyDrivers(params = {}) {
    try {
      const response = await apiService.get('/fleet/drivers', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getDriverById(driverId) {
    try {
      const response = await apiService.get(`/api/fleet/drivers/${driverId}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async addDriver(driverData) {
    try {
      const response = await apiService.post('/fleet/drivers', driverData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateDriver(driverId, driverData) {
    try {
      const response = await apiService.put(`/api/fleet/drivers/${driverId}`, driverData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async removeDriver(driverId) {
    try {
      const response = await apiService.delete(`/api/fleet/drivers/${driverId}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateDriverStatus(driverId, statusData) {
    try {
      const response = await apiService.put(`/api/fleet/drivers/${driverId}/status`, statusData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getDriverPerformance(driverId, params = {}) {
    try {
      const response = await apiService.get(`/api/fleet/drivers/${driverId}/performance`, params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // FLEET AMBULANCES MANAGEMENT
  // ============================================================================

  async getMyAmbulances(params = {}) {
    try {
      return await apiService.getMyVehicles(params);
    } catch (error) {
      return await apiService.getAmbulances(params);
    }
  }

  async getAmbulanceById(ambulanceId) {
    try {
      return await apiService.getAmbulanceById(ambulanceId);
    } catch (error) {
      throw error;
    }
  }

  async addAmbulance(ambulanceData) {
    try {
      return await apiService.registerAmbulance(ambulanceData);
    } catch (error) {
      throw error;
    }
  }

  async updateAmbulance(ambulanceId, ambulanceData) {
    try {
      return await apiService.updateAmbulance(ambulanceId, ambulanceData);
    } catch (error) {
      throw error;
    }
  }

  async removeAmbulance(ambulanceId) {
    try {
      return await apiService.deleteAmbulance(ambulanceId);
    } catch (error) {
      throw error;
    }
  }

  async updateAmbulanceStatus(ambulanceId, statusData) {
    try {
      return await apiService.updateAmbulanceStatus(ambulanceId, statusData);
    } catch (error) {
      throw error;
    }
  }

  async assignDriverToAmbulance(ambulanceId, driverId) {
    try {
      return await apiService.assignDriverToAmbulance(ambulanceId, { driver_id: driverId });
    } catch (error) {
      throw error;
    }
  }

  async removeDriverFromAmbulance(ambulanceId) {
    try {
      return await apiService.removeDriverFromAmbulance(ambulanceId);
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // FLEET BOOKINGS MANAGEMENT
  // ============================================================================

  async getMyBookings(params = {}) {
    try {
      const response = await apiService.get('/fleet/bookings', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getBookingById(bookingId) {
    try {
      const response = await apiService.get(`/api/fleet/bookings/${bookingId}`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateBookingStatus(bookingId, statusData) {
    try {
      const response = await apiService.put(`/api/fleet/bookings/${bookingId}/status`, statusData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async assignDriverToBooking(bookingId, driverId) {
    try {
      const response = await apiService.post(`/api/fleet/bookings/${bookingId}/assign-driver`, {
        driverId
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async assignAmbulanceToBooking(bookingId, ambulanceId) {
    try {
      const response = await apiService.post(`/api/fleet/bookings/${bookingId}/assign-ambulance`, {
        ambulanceId
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getBookingAnalytics(params = {}) {
    try {
      const response = await apiService.get('/fleet/bookings/analytics', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // FLEET REPORTS
  // ============================================================================

  async generateReport(reportType, params = {}) {
    try {
      const response = await apiService.post('/fleet/reports/generate', {
        reportType,
        ...params
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getReportHistory(params = {}) {
    try {
      const response = await apiService.get('/fleet/reports/history', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async downloadReport(reportId) {
    try {
      const response = await apiService.get(`/api/fleet/reports/${reportId}/download`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // FLEET SETTINGS
  // ============================================================================

  async getSettings() {
    try {
      const response = await apiService.get('/fleet/settings');
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateSettings(settingsData) {
    try {
      const response = await apiService.put('/fleet/settings', settingsData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getServiceAreas() {
    try {
      const response = await apiService.get('/fleet/service-areas');
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateServiceAreas(serviceAreasData) {
    try {
      const response = await apiService.put('/fleet/service-areas', serviceAreasData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getEmergencyContacts() {
    try {
      const response = await apiService.get('/fleet/emergency-contacts');
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateEmergencyContacts(contactsData) {
    try {
      const response = await apiService.put('/fleet/emergency-contacts', contactsData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // FLEET NOTIFICATIONS
  // ============================================================================

  async getNotifications(params = {}) {
    try {
      const response = await apiService.get('/fleet/notifications', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async markNotificationAsRead(notificationId) {
    try {
      const response = await apiService.put(`/api/fleet/notifications/${notificationId}/read`);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async markAllNotificationsAsRead() {
    try {
      const response = await apiService.put('/fleet/notifications/read-all');
      return response;
    } catch (error) {
      throw error;
    }
  }

  // ============================================================================
  // FLEET MAINTENANCE
  // ============================================================================

  async getMaintenanceSchedule(params = {}) {
    try {
      const response = await apiService.get('/fleet/maintenance/schedule', params);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async scheduleMaintenance(maintenanceData) {
    try {
      const response = await apiService.post('/fleet/maintenance/schedule', maintenanceData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async updateMaintenanceStatus(maintenanceId, statusData) {
    try {
      const response = await apiService.put(`/api/fleet/maintenance/${maintenanceId}/status`, statusData);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getMaintenanceHistory(params = {}) {
    try {
      const response = await apiService.get('/fleet/maintenance/history', params);
      return response;
    } catch (error) {
      throw error;
    }
  }
}

// Create and export singleton instance
const fleetService = new FleetService();
export default fleetService;
